import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, LoadingSpinner, ViewPageHeader, Button, ResourcePreview } from '@components/common';
import { RefillModal } from '@components/modals';
import { useTranslation } from '@hooks/useTranslation';
import { useNotification } from '@hooks/useNotification';
import { formatDate } from '@utils/dateUtils';
import Icon from '@components/Icon/Icon';
import api from '@services/api';
import './RefillViewPage.scss';

const RefillViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const notify = useNotification();
    const [refill, setRefill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, mode: 'delete' });

    useEffect(() => {
        const fetchRefill = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/refills/${id}`);
                setRefill(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch refill:', err);
                setError(t('refillView.fetchError'));
                notify.error(t('refillView.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchRefill();
        }
    }, [id, t]);

    const handleBack = () => {
        navigate('/refills');
    };

    const handleEdit = () => {
        navigate(`/refills/${id}/edit`);
    };

    const handleDelete = () => {
        setModalState({ isOpen: true, mode: 'delete' });
    };

    const handleModalClose = () => {
        setModalState({ isOpen: false, mode: 'delete' });
    };

    const handleModalSuccess = () => {
        if (modalState.mode === 'delete') {
            navigate('/refills');
        }
    };

    if (loading) {
        return (
            <div className="refill-view-page">
                <div className="loading-container">
                    <LoadingSpinner />
                    <p>{t('refillView.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !refill) {
        return (
            <div className="refill-view-page">
                <Card className="error-card">
                    <div className="error-content">
                        <h2>{t('refillView.errorTitle')}</h2>
                        <p>{error || t('refillView.notFound')}</p>
                    </div>
                </Card>
            </div>
        );
    }
    console.log('🚀 ~ refill.resource:', refill.resource);

    const unit = refill.resource.resource_metadata?.find(meta => meta.metadata_key === 'unit')?.value;

    return (
        <div className="refill-view-page">
            <ViewPageHeader
                onBack={handleBack}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <div className="refill-details">
                <Card className="refill-info-card" style={{ animation: 'slideInUp 0.6s ease-out' }}>
                    <div className="refill-header">
                        <div className="refill-icon">
                            <Icon icon="arrow-up" size="lg" color="#28a745" />
                        </div>
                        <div className="refill-info">
                            <h1>{t('refillView.refillDetails')}</h1>
                            <div className="quantity-added">
                                +{refill.quantity_added} {unit || t('refillView.unitsAdded')}
                            </div>
                        </div>
                    </div>

                    {/* Resource Preview Section */}
                    {refill.resource && (
                        <ResourcePreview
                            resource={refill.resource}
                            noImageText={t('common.noImage')}
                        />
                    )}

                    <div className="refill-metadata">
                        {refill.resource && (
                            <div className="metadata-item">
                                <div className="metadata-icon">
                                    <Icon icon="cubes" size="sm" />
                                </div>
                                <span className="label">{t('refillView.resource')}:</span>
                                <span className="value">{refill.resource.name}</span>
                            </div>
                        )}
                        {refill.user && (
                            <div className="metadata-item">
                                <div className="metadata-icon">
                                    <Icon icon="user" size="sm" />
                                </div>
                                <span className="label">{t('refillView.refiller')}:</span>
                                <span className="value">{refill.user.name || refill.user.email}</span>
                            </div>
                        )}
                        <div className="metadata-item">
                            <div className="metadata-icon">
                                <Icon icon="arrow-up" size="sm" />
                            </div>
                            <span className="label">{t('refillView.quantityAdded')}:</span>
                            <span className="value">{refill.quantity_added}</span>
                        </div>
                        {refill.refilled_at && (
                            <div className="metadata-item">
                                <div className="metadata-icon">
                                    <Icon icon="calendar" size="sm" />
                                </div>
                                <span className="label">{t('refillView.refilledAt')}:</span>
                                <span className="value">
                                    {formatDate(refill.refilled_at, true, i18n.language, t)}
                                </span>
                            </div>
                        )}
                        {refill.notes && (
                            <div className="metadata-item notes">
                                <div className="notes-header">
                                    <div className="metadata-icon">
                                        <Icon icon="file-alt" size="sm" />
                                    </div>
                                    <span className="label">{t('refillView.notes')}:</span>
                                </div>
                                <div className="value">{refill.notes}</div>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            <RefillModal
                isOpen={modalState.isOpen}
                onClose={handleModalClose}
                mode="delete"
                refill={refill}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default RefillViewPage;
